import Grid from "@material-ui/core/Grid";
import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import EyeIcon from "mdi-material-ui/EyeCircle";
import { withSnackbar } from "notistack";
import { connect } from "react-redux";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

import DivisionsTable from "../../components/Tables/Divisons";
import Header from "../../components/Typography/Header";
import findDivisionRelations from "../../utils/findDivisionRelations";
import PropertyDivisionTransferDialog from "../../components/Dialogs/PropertyDivisionTransfer";
import UserDivisionTransferDialog from "../../components/Dialogs/UserDivisionTransfer";
import UpdateDivisionDialog from "../../components/Dialogs/UpdateDivision";
import CreateDivisionDialog from "../../components/Dialogs/CreateDivision";
import PropertiesTransferDialog from "../../components/Dialogs/PropertiesDivisionTransfer";
import UsersTransferDialog from "../../components/Dialogs/UsersDivisionTransfer";
import { updateDivision } from "../../utils/appsyncOperations";
import LoadingButton from "../../components/Buttons/Loading";
import * as actions from "../../store/actions";
import { isUserRole } from "../../utils/user";

const styles = {
  menu: {
    width: 200,
  },
  progress: {
    height: 4,
  },
  actions: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
};

const divisionless = {
  id: "NONE",
  displayName: "None",
};

const DivisionsContent = ({
  divisions,
  serviceProviderId,
  properties,
  users,
  enqueueSnackbar,
  dispatch,
}) => {
  const [showEditName, setShowEditName] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [divisionId, setDivisionId] = useState(divisionless.id);
  const [data, setData] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const classes = useStyles();

  useEffect(() => {
    const tempData = [];
    if (divisionId === divisionless.id) {
      // Get items without division
      for (const property of Object.values(properties)) {
        if (
          !property.isDeleted &&
          Object.keys(findDivisionRelations(property.propertyId).included)
            .length === 0
        ) {
          tempData.push({
            type: "Property",
            displayName: properties[property.propertyId].displayName,
            id: property.propertyId,
            actions: (
              <span style={styles.actions}>
                <IconButton
                  onClick={() => setSelectedPropertyId(property.propertyId)}
                >
                  <EyeIcon />
                </IconButton>
              </span>
            ),
          });
        }
      }
      for (const user of Object.values(users)) {
        if (
          !user.isDeleted &&
          Object.keys(findDivisionRelations(user.userId).included).length === 0
        ) {
          tempData.push({
            type: "User",
            displayName: users[user.userId].displayName,
            id: user.userId,
            actions: (
              <span style={styles.actions}>
                <IconButton onClick={() => setSelectedUserId(user.userId)}>
                  <EyeIcon />
                </IconButton>
              </span>
            ),
          });
        }
      }
    } else {
      // Get items for selected division
      for (const division of Object.values(divisions).filter(
        (division) => division.divisionId === divisionId
      )) {
        for (const user of Object.values(division.users.byId)) {
          if (user.isDeleted) continue;
          tempData.push({
            type: "User",
            displayName: users[user.relationalId].displayName,
            division: division.displayName,
            id: `${user.relationalId},${user.divisionId}`,
            actions: (
              <span style={styles.actions}>
                <IconButton
                  onClick={() => setSelectedUserId(user.relationalId)}
                >
                  <EyeIcon />
                </IconButton>
              </span>
            ),
          });
        }
        for (const property of Object.values(division.properties.byId)) {
          if (property.isDeleted) continue;
          if (properties[property.relationalId]) {
            tempData.push({
              type: "Property",
              displayName: properties[property.relationalId].displayName,
              division: division.displayName,
              id: `${property.relationalId},${property.divisionId}`,
              actions: (
                <span style={styles.actions}>
                  <IconButton
                    onClick={() => setSelectedPropertyId(property.relationalId)}
                  >
                    <EyeIcon />
                  </IconButton>
                </span>
              ),
            });
          } else {
            console.log(property);
          }
        }
      }
    }
    setData(tempData);
  }, [divisionId, divisions]);

  const deleteDivision = () => {
    setDeletePending(true);
    updateDivision({ divisionId, isDeleted: true }).then(
      (deletedDivision) => {
        setDeletePending(false);
        setShowDelete(false);
        setDivisionId(divisionless.id);
        dispatch(actions.divisions.setDivision(deletedDivision));
        enqueueSnackbar("Deletion successful!", { variant: "success" });
      },
      (error) => {
        setDeletePending(false);
        enqueueSnackbar(error, { variant: "error" });
      }
    );
  };

  const openDeleteDialog = () => {
    const division = divisions[divisionId];
    const divisionPropertyCount = Object.values(
      division.properties.byId
    ).filter((divisionRelation) => !divisionRelation.isDeleted).length;
    const divisionUserCount = Object.values(division.users.byId).filter(
      (divisionRelation) => !divisionRelation.isDeleted
    ).length;
    if (divisionPropertyCount === 0 && divisionUserCount === 0) {
      setShowDelete(true);
    } else {
      enqueueSnackbar(
        "You cannot delete this division until all assigned properties and users have been removed.",
        { variant: "error" }
      );
    }
  };

  const isAdmin = isUserRole({ admin: true });

  return (
    <div className={classes.root}>
      <div className={classes.scrollable}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item s={12} xs={12}>
              <Header
                label={"Divisions"}
                action={
                  isAdmin
                    ? {
                        title: "Create",
                        onClick: () => setShowCreate(true),
                      }
                    : null
                }
              />
            </Grid>
            <Grid item s={12} xs={12}>
              <TextField
                select
                label="Division"
                value={divisionId}
                onChange={(e) => setDivisionId(e.target.value)}
                SelectProps={{
                  MenuProps: {
                    style: styles.menus,
                  },
                }}
                fullWidth
                variant="outlined"
              >
                {Object.values({
                  ...divisions,
                  [divisionless.id]: {
                    divisionId: divisionless.id,
                    displayName: divisionless.displayName,
                  },
                })
                  .filter((division) => !division.isDeleted)
                  .map((item) => (
                    <MenuItem key={item.divisionId} value={item.divisionId}>
                      {item.displayName}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item lg={12} xs={12}>
              <DivisionsTable data={data} />
            </Grid>
            {divisionId !== divisionless.id && isAdmin && (
              <Grid className={classes.buttonContainer} item lg={12} xs={12}>
                <Button color="primary" onClick={() => setShowProperties(true)}>
                  Properties
                </Button>
                <Button color="primary" onClick={() => setShowUsers(true)}>
                  Users
                </Button>
                <Button color="primary" onClick={() => setShowEditName(true)}>
                  Name
                </Button>
                <Button color="primary" onClick={openDeleteDialog}>
                  Delete
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      </div>
      {divisionId !== divisionless.id && (
        <UpdateDivisionDialog
          open={showEditName}
          onClose={() => setShowEditName(false)}
          division={divisions[divisionId]}
        />
      )}
      <UsersTransferDialog
        open={showUsers}
        onClose={() => setShowUsers(false)}
        divisionId={divisionId}
      />
      <PropertiesTransferDialog
        open={showProperties}
        onClose={() => setShowProperties(false)}
        divisionId={divisionId}
      />
      <PropertyDivisionTransferDialog
        open={selectedPropertyId !== null}
        onClose={() => setSelectedPropertyId(null)}
        propertyId={selectedPropertyId}
      />
      <UserDivisionTransferDialog
        open={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
        userId={selectedUserId}
      />
      <CreateDivisionDialog
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        serviceProviderId={serviceProviderId}
      />
      {divisionId !== divisionless.id && (
        <Dialog open={showDelete} onClose={() => setShowDelete(false)}>
          <DialogTitle>Delete division</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the selected division?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              isLoading={deletePending}
              color="primary"
              onClick={deleteDivision}
            >
              Confirm
            </LoadingButton>
            <Button color="primary" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    position: "relative",
  },
  scrollable: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    overflowX: "hidden",
    overflowY: "auto",
  },
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(3),
  },
  buttonContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
}));

const mapStateToProps = (state) => ({
  divisions: state.divisions.byId,
  serviceProviderId: state.auth.user.attributes["custom:service_provider_id"],
  properties: state.properties.byId,
  users: state.users.byId,
});

export default withSnackbar(connect(mapStateToProps)(DivisionsContent));
